import { useCallback, useEffect, useRef, useState } from "react"
import { ActivityIndicator, Animated, StyleSheet, Text, View } from "react-native"

import { Image } from "expo-image"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { ChannelBrandData } from "src/services/api/api.types"

const DISPLAY_DURATION_MS = 1800
const FADE_OUT_DURATION_MS = 200
const HOLD_DURATION_MS = DISPLAY_DURATION_MS
const IMAGE_MAX_WAIT_MS = 5000

/** Returns true when hex is light enough that dark text should be used. */
function isLightColor(hex: string): boolean {
  const c = hex.replace("#", "")
  if (c.length < 6) return true
  const r = parseInt(c.substring(0, 2), 16)
  const g = parseInt(c.substring(2, 4), 16)
  const b = parseInt(c.substring(4, 6), 16)
  // Relative luminance approximation (same weights as portal BrandingPanel)
  return (r * 299 + g * 587 + b * 114) / 1000 > 128
}

function getImageIdentity(uri?: string | null): string | null {
  if (!uri) return null
  try {
    // Ignore signed query params so same underlying file doesn't trigger reload.
    return new URL(uri).pathname
  } catch {
    return uri.split("?")[0]
  }
}

interface ChannelSplashProps {
  // Nullable: splash shows immediately as a white screen while data is still loading
  brandData: ChannelBrandData | null
  onDismiss: () => void
}

/**
 * Full-screen channel branding splash shown when entering a channel.
 * Uses the channel brand color as background (falls back to white).
 * Renders immediately (even with null brandData) to block the chat from showing first.
 * Waits up to 5 seconds for the brand image to load, then starts the display timer.
 */
export function ChannelSplash({ brandData, onDismiss }: ChannelSplashProps) {
  const insets = useSafeAreaInsets()
  const opacity = useRef(new Animated.Value(1)).current
  const didDismiss = useRef(false)

  const [displayBrandImageUri, setDisplayBrandImageUri] = useState<string | null>(
    brandData?.brand_image ?? null,
  )
  const imageIdentityRef = useRef<string | null>(getImageIdentity(brandData?.brand_image))
  const [imageLoading, setImageLoading] = useState(!!(brandData?.brand_image ?? null))
  const [imageReady, setImageReady] = useState(false)

  // Coalesce empty strings to null so we don't render `""` as a text-node
  // child via `{value && <Element/>}` short-circuit (rn-web "Unexpected text
  // node" warning).
  const displayTitle: string | null = brandData
    ? brandData.brand_title || brandData.name || null
    : null
  const displaySubtitle: string | null = brandData?.brand_subtitle || null
  const brandColor =
    brandData?.brand_color && /^#[0-9A-Fa-f]{6}$/.test(brandData.brand_color)
      ? brandData.brand_color
      : "#FFFFFF"
  const lightBackground = isLightColor(brandColor)
  const titleColor = lightBackground ? "#000000" : "#FFFFFF"
  const subtitleColor = lightBackground ? "#000000" : "#FFFFFF"

  const dismissSplash = useCallback(() => {
    if (didDismiss.current) return
    didDismiss.current = true
    onDismiss()
  }, [onDismiss])

  useEffect(() => {
    const waitTimer = setTimeout(() => {
      if (!imageReady) {
        dismissSplash()
      }
    }, IMAGE_MAX_WAIT_MS)

    return () => {
      clearTimeout(waitTimer)
    }
  }, [dismissSplash, imageReady])

  useEffect(() => {
    if (!imageReady || didDismiss.current) return

    const sequence = Animated.sequence([
      Animated.delay(HOLD_DURATION_MS),
      Animated.timing(opacity, {
        toValue: 0,
        duration: FADE_OUT_DURATION_MS,
        useNativeDriver: true,
      }),
    ])

    sequence.start(() => {
      dismissSplash()
    })

    return () => {
      sequence.stop()
    }
  }, [dismissSplash, imageReady, opacity])

  // Reload image only when the underlying image identity changes.
  // Signed URL/query-string changes for the same file should not restart loading.
  useEffect(() => {
    const nextUri = brandData?.brand_image ?? null
    const nextIdentity = getImageIdentity(nextUri)

    if (nextIdentity === imageIdentityRef.current) return

    imageIdentityRef.current = nextIdentity
    setDisplayBrandImageUri(nextUri)
    setImageLoading(!!nextUri)
    setImageReady(false)
  }, [brandData?.brand_image])

  return (
    <Animated.View style={[styles.container, { opacity, backgroundColor: brandColor }]}>
      <View
        style={[
          styles.content,
          {
            paddingTop: insets.top + 40,
            paddingBottom: insets.bottom + 40,
            paddingHorizontal: 40,
          },
        ]}
      >
        {displayBrandImageUri ? (
          <View style={styles.brandImage}>
            {imageLoading && (
              <ActivityIndicator style={StyleSheet.absoluteFill} size="large" color={titleColor} />
            )}
            <Image
              source={{ uri: displayBrandImageUri }}
              style={[StyleSheet.absoluteFill, imageLoading && styles.hidden]}
              cachePolicy="memory-disk"
              contentFit="contain"
              accessibilityLabel={`Logo for ${displayTitle}`}
              accessibilityRole="image"
              onLoadStart={() => setImageLoading(true)}
              onLoad={() => {
                setImageLoading(false)
                setImageReady(true)
              }}
              onError={() => setImageLoading(false)}
            />
          </View>
        ) : (
          <View style={styles.noImageSpacer} />
        )}

        {displayTitle ? (
          <View style={styles.textContainer}>
            <Text
              style={[styles.title, { color: titleColor }]}
              numberOfLines={2}
              adjustsFontSizeToFit
            >
              {displayTitle.toUpperCase()}
            </Text>
            {displaySubtitle ? (
              <Text
                style={[styles.subtitle, { color: subtitleColor }]}
                numberOfLines={2}
                adjustsFontSizeToFit
              >
                {displaySubtitle.toUpperCase()}
              </Text>
            ) : null}
          </View>
        ) : null}
      </View>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "#FFFFFF",
    zIndex: 9999,
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    flex: 1,
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  brandImage: {
    width: "70%",
    aspectRatio: 1,
    maxWidth: 280,
    maxHeight: 280,
  },
  noImageSpacer: {
    height: 40,
  },
  hidden: {
    opacity: 0,
  },
  textContainer: {
    marginTop: 24,
    alignItems: "center",
    paddingHorizontal: 16,
  },
  title: {
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 12,
    textAlign: "center",
    marginTop: 4,
    letterSpacing: 0.5,
  },
})
